import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React, { useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCreateSignupMutation } from '../../api/api-hooks';
import { SignupForm } from './authTypes';
import { getPasswordStrength, validateSignupForm } from './authUtils';
import logo from './logoCompressed.webp';

const initialFormState: SignupForm = {
  name: undefined,
  phone: undefined,
  address: undefined,
  email: undefined,
  password: undefined,
  analyzerSerial: undefined,
  company: undefined,
};

const RequestAccess = () => {
  const [requestAccess] = useCreateSignupMutation();
  const [formState, setFormState] = useReducer(
    (curVals: any, newVals: any) => ({ ...curVals, ...newVals }),
    initialFormState
  );
  const [passwordScore, setPasswordScore] = useState<number>(0);
  const { push } = useHistory();
  const [submitMessage, setSubmitMessage] = useState<string>('');
  const [passwordTooShort, setPasswordTooShort] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    setFormState({ [name]: value });
    if (name === 'password') {
      import('zxcvbn').then((check) => {
        const { score } = check.default(value);
        setPasswordScore(score);
      });
      if (value.length >= 8) setPasswordTooShort(false);
      else setPasswordTooShort(true);
    }
  };
  const handleSubmit = async () => {
    const response = await requestAccess({
      createSignupDto: {
        ...formState,
      },
    });
    if ('data' in response) {
      setSubmitMessage('Request received.');
    } else {
      setSubmitMessage('Something went wrong. Please try again later.');
    }
  };
  const handleShowPassword = () => {
    setShowPassword((state) => !state);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Grid container direction='column' spacing={3}>
      <Grid item>
        <Typography variant='h6'>Request Client Access</Typography>
      </Grid>
      <Grid container item justifyContent='center' alignItems='center'>
        <Grid
          container
          xs
          item
          direction='column'
          spacing={1}
          alignItems='flex-end'
        >
          <Grid item>
            <TextField
              name='name'
              value={formState.name}
              variant='outlined'
              label='Name'
              style={{ width: '400px' }}
              onChange={handleChange}
            />
          </Grid>
          <Grid item>
            <TextField
              name='phone'
              value={formState.phone}
              variant='outlined'
              label='Phone #'
              style={{ width: '400px' }}
              onChange={handleChange}
            />
          </Grid>
          <Grid item>
            <TextField
              name='address'
              value={formState.address}
              variant='outlined'
              label='Address'
              style={{ width: '400px' }}
              onChange={handleChange}
            />
          </Grid>
          <Grid item>
            <TextField
              name='email'
              value={formState.email}
              variant='outlined'
              label='Email'
              style={{ width: '400px' }}
              onChange={handleChange}
            />
          </Grid>
          <Grid item>
            <TextField
              name='company'
              value={formState.company}
              variant='outlined'
              label='Company'
              style={{ width: '400px' }}
              onChange={handleChange}
            />
          </Grid>
          <Grid item>
            <TextField
              name='analyzerSerial'
              value={formState.analyzerSerial}
              variant='outlined'
              label='Analyzer Serial #'
              helperText='Please enter the serial number of an analyzer your company owns to help us verify your identity'
              style={{ width: '400px' }}
              onChange={handleChange}
            />
          </Grid>
          <Grid item>
            <FormControl variant='outlined'>
              <InputLabel htmlFor='outlined-adornment-password'>
                Password
              </InputLabel>
              <OutlinedInput
                id='outlined-adornment-password'
                type={showPassword ? 'text' : 'password'}
                value={formState.password}
                onChange={handleChange}
                name='password'
                style={{ width: '400px' }}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                      size='large'
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Grid>
          <Grid item>
            <Typography style={{ marginRight: '6rem' }}>
              <strong>Password Strength</strong>:{' '}
              {getPasswordStrength(passwordScore)}
            </Typography>
          </Grid>
          {passwordTooShort && (
            <Grid item>
              <Typography color='error' style={{ marginRight: '2.2rem' }}>
                Password must be at least 8 characters long.
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid
          container
          item
          justifyContent='flex-start'
          xs
          style={{ marginLeft: '2rem' }}
        >
          <Grid item>
            <img src={logo} width='400px' alt='LP Technologies Logo' />
          </Grid>
        </Grid>
      </Grid>
      <Grid item container spacing={2} justifyContent='center'>
        <Grid item>
          <Button
            variant='contained'
            color='primary'
            disabled={
              !validateSignupForm(formState) ||
              !!submitMessage ||
              passwordTooShort
            }
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => {
              push('/');
            }}
          >
            Back to Login
          </Button>
        </Grid>
      </Grid>
      {!validateSignupForm(formState) && (
        <Grid item>
          <Typography color='error'>
            Name, Phone, Email, Company, and Password are required.
          </Typography>
        </Grid>
      )}
      {submitMessage && (
        <Grid item>
          <Typography>{submitMessage}</Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default RequestAccess;
